import { baseApi } from "@/api/baseApi";
import { CONTRACT_FAMILIES_TAGS } from "@/api/contract-families.constraints";
import {
  CommentPayload,
  ContractFamilyExpansionResponse,
  ContractFamilyCopyResponse,
  ContractFamilyDocumentAddPayload,
  ContractFamilyDocumentJobResponse,
  ContractFamilyListResponse,
  ContractFamilyReviewQueueStatus,
  ContractFamilyProjectionReadinessResponse,
  FamilySummaryJobCreatePayload,
  FamilySummaryJobCreateResponse,
  FamilySummaryJobResponse,
  FamilySummaryFieldListResponse,
  FamilySummaryListResponse,
  GroupingJobCreatePayload,
  GroupingJobCreateResponse,
  GroupingJobResponse,
  LinkPayload,
  ManualOverrideResponse,
  MetadataPatchPayload,
  ReviewQueueResponse,
  RoleUpdatePayload,
} from "@/types/contract-family";

const CONTRACT_FAMILIES_BASE_URL = "/pf-mainweb/document/contract-families";

interface GetContractFamiliesParams {
  folder_fse_id?: string | null;
  pipeline_run_id?: string | null;
}

interface GetContractFamilyReviewQueueParams {
  status?: ContractFamilyReviewQueueStatus | null;
  folder_fse_id?: string | null;
  pipeline_run_id?: string | null;
}

interface ManualMutationParams<TPayload> {
  documentId: string;
  payload: TPayload;
}

export const manualOverrideInvalidationTags = [
  CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILIES_TAG,
  CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_REVIEW_QUEUE_TAG,
  CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_GROUPING_JOB_TAG,
  CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARIES_TAG,
];

export const contractFamilyRequests = {
  families: (params?: GetContractFamiliesParams | void) => ({
    url: `${CONTRACT_FAMILIES_BASE_URL}/`,
    method: "GET" as const,
    params: {
      folder_fse_id: params?.folder_fse_id ?? undefined,
      pipeline_run_id: params?.pipeline_run_id ?? undefined,
    },
  }),
  reviewQueue: (params?: GetContractFamilyReviewQueueParams | void) => ({
    url: `${CONTRACT_FAMILIES_BASE_URL}/review-queue`,
    method: "GET" as const,
    params: {
      status: params?.status ?? undefined,
      folder_fse_id: params?.folder_fse_id ?? undefined,
      pipeline_run_id: params?.pipeline_run_id ?? undefined,
    },
  }),
};

export const contractFamiliesApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    createGroupingJob: builder.mutation<GroupingJobCreateResponse, GroupingJobCreatePayload>({
      invalidatesTags: result => [
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILIES_TAG,
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_REVIEW_QUEUE_TAG,
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_GROUPING_JOB_TAG,
        ...(result ? [{ type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_GROUPING_JOB_TAG, id: result.tx_id }] : []),
      ],
      query: payload => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/grouping-jobs`,
        method: "POST",
        data: payload,
      }),
    }),
    getGroupingJob: builder.query<GroupingJobResponse, string>({
      providesTags: (_result, _error, txId) => [
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_GROUPING_JOB_TAG,
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_GROUPING_JOB_TAG, id: txId },
      ],
      query: txId => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/grouping-jobs/${txId}`,
        method: "GET",
      }),
    }),
    getContractFamilies: builder.query<ContractFamilyListResponse, GetContractFamiliesParams | void>({
      providesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILIES_TAG],
      query: contractFamilyRequests.families,
    }),
    getContractFamilyReviewQueue: builder.query<ReviewQueueResponse, GetContractFamilyReviewQueueParams | void>({
      providesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_REVIEW_QUEUE_TAG],
      query: contractFamilyRequests.reviewQueue,
    }),
    copyContractFamily: builder.mutation<ContractFamilyCopyResponse, string>({
      invalidatesTags: [
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILIES_TAG,
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARIES_TAG,
      ],
      query: familyId => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/families/${familyId}/copy`,
        method: "POST",
      }),
    }),
    deleteContractFamilyCopy: builder.mutation<void, string>({
      invalidatesTags: [
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILIES_TAG,
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARIES_TAG,
      ],
      query: copiedFamilyId => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/families/${copiedFamilyId}/copy`,
        method: "DELETE",
      }),
    }),
    renameContractFamily: builder.mutation<{ family_id: string; name: string }, { familyId: string; name: string }>({
      invalidatesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILIES_TAG],
      query: ({ familyId, name }) => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/families/${familyId}`,
        method: "PATCH",
        data: { name },
      }),
    }),
    addContractFamilyDocument: builder.mutation<ContractFamilyDocumentJobResponse, ContractFamilyDocumentAddPayload>({
      invalidatesTags: [
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILIES_TAG,
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_REVIEW_QUEUE_TAG,
      ],
      query: payload => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/documents`,
        method: "POST",
        data: payload,
      }),
    }),
    getContractFamilyDocumentJob: builder.query<ContractFamilyDocumentJobResponse, string>({
      providesTags: (_result, _error, txId) => [
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_GROUPING_JOB_TAG,
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_GROUPING_JOB_TAG, id: txId },
      ],
      query: txId => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/document-jobs/${txId}`,
        method: "GET",
      }),
    }),
    createFamilySummaryJob: builder.mutation<FamilySummaryJobCreateResponse, FamilySummaryJobCreatePayload>({
      invalidatesTags: result => [
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARIES_TAG,
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARY_JOB_TAG,
        ...(result ? [{ type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARY_JOB_TAG, id: result.tx_id }] : []),
      ],
      query: payload => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/summary-jobs`,
        method: "POST",
        data: payload,
      }),
    }),
    getFamilySummaryJob: builder.query<FamilySummaryJobResponse, string>({
      providesTags: (_result, _error, txId) => [
        CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARY_JOB_TAG,
        { type: CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARY_JOB_TAG, id: txId },
      ],
      query: txId => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/summary-jobs/${txId}`,
        method: "GET",
      }),
    }),
    getFamilySummaries: builder.query<FamilySummaryListResponse, string>({
      providesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARIES_TAG],
      query: snapshotCollectionId => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/summaries`,
        method: "GET",
        params: { snapshot_collection_id: snapshotCollectionId },
      }),
    }),
    getFamilySummaryFields: builder.query<FamilySummaryFieldListResponse, string>({
      providesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARIES_TAG],
      query: snapshotCollectionId => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/summary-fields`,
        method: "GET",
        params: { snapshot_collection_id: snapshotCollectionId },
      }),
    }),
    getContractFamilyProjectionReadiness: builder.query<ContractFamilyProjectionReadinessResponse, string>({
      providesTags: [CONTRACT_FAMILIES_TAGS.CONTRACT_FAMILY_SUMMARIES_TAG],
      query: snapshotCollectionId => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/collections/${snapshotCollectionId}/projection-readiness`,
        method: "GET",
      }),
    }),
    getContractFamilyExpansion: builder.query<
      ContractFamilyExpansionResponse,
      { snapshotCollectionId: string; familyId: string }
    >({
      query: ({ snapshotCollectionId, familyId }) => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/collections/${snapshotCollectionId}/families/${familyId}/expansion`,
        method: "GET",
      }),
    }),
    updateContractFamilyMetadata: builder.mutation<ManualOverrideResponse, ManualMutationParams<MetadataPatchPayload>>({
      invalidatesTags: manualOverrideInvalidationTags,
      query: ({ documentId, payload }) => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/documents/${documentId}/metadata`,
        method: "PATCH",
        data: payload,
      }),
    }),
    overrideContractFamilyRole: builder.mutation<ManualOverrideResponse, ManualMutationParams<RoleUpdatePayload>>({
      invalidatesTags: manualOverrideInvalidationTags,
      query: ({ documentId, payload }) => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/documents/${documentId}/role`,
        method: "POST",
        data: payload,
      }),
    }),
    linkContractFamilyDocument: builder.mutation<ManualOverrideResponse, ManualMutationParams<LinkPayload>>({
      invalidatesTags: manualOverrideInvalidationTags,
      query: ({ documentId, payload }) => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/documents/${documentId}/link`,
        method: "POST",
        data: payload,
      }),
    }),
    markContractFamilyOrphan: builder.mutation<ManualOverrideResponse, ManualMutationParams<CommentPayload>>({
      invalidatesTags: manualOverrideInvalidationTags,
      query: ({ documentId, payload }) => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/documents/${documentId}/mark-orphan`,
        method: "POST",
        data: payload,
      }),
    }),
    unlinkContractFamilyDocument: builder.mutation<ManualOverrideResponse, ManualMutationParams<CommentPayload>>({
      invalidatesTags: manualOverrideInvalidationTags,
      query: ({ documentId, payload }) => ({
        url: `${CONTRACT_FAMILIES_BASE_URL}/documents/${documentId}/unlink`,
        method: "POST",
        data: payload,
      }),
    }),
  }),
});

export const {
  useCreateGroupingJobMutation,
  useGetGroupingJobQuery,
  useLazyGetGroupingJobQuery,
  useGetContractFamiliesQuery,
  useLazyGetContractFamiliesQuery,
  useGetContractFamilyReviewQueueQuery,
  useLazyGetContractFamilyReviewQueueQuery,
  useCopyContractFamilyMutation,
  useDeleteContractFamilyCopyMutation,
  useRenameContractFamilyMutation,
  useAddContractFamilyDocumentMutation,
  useGetContractFamilyDocumentJobQuery,
  useCreateFamilySummaryJobMutation,
  useGetFamilySummaryJobQuery,
  useLazyGetFamilySummaryJobQuery,
  useGetFamilySummariesQuery,
  useGetFamilySummaryFieldsQuery,
  useGetContractFamilyProjectionReadinessQuery,
  useGetContractFamilyExpansionQuery,
  useLazyGetFamilySummariesQuery,
  useUpdateContractFamilyMetadataMutation,
  useOverrideContractFamilyRoleMutation,
  useLinkContractFamilyDocumentMutation,
  useMarkContractFamilyOrphanMutation,
  useUnlinkContractFamilyDocumentMutation,
} = contractFamiliesApi;
